<script setup lang="ts">
const { t } = useI18n();

const featureKeys = ['electronic', 'multiParty', 'tracking', 'certificate'] as const;
const featureIcons: Record<typeof featureKeys[number], string> = {
  electronic: "lucide:pen-tool",
  multiParty: "lucide:users",
  tracking: "lucide:bell-ring",
  certificate: "lucide:shield-check",
};
const features = computed(() => featureKeys.map(key => ({
  title: t(`signature.${key}.title`),
  description: t(`signature.${key}.description`),
  icon: featureIcons[key],
})));
</script>

<template>
  <div
    id="signature"
    class="my-24 scroll-mt-10 sm:my-32 xl:my-48"
  >
    <div class="mx-auto max-w-7xl px-6 lg:px-8">
      <div class="mx-auto grid max-w-2xl grid-cols-1 gap-x-8 gap-y-16 sm:gap-y-20 lg:mx-0 lg:max-w-none lg:grid-cols-2 lg:items-center">
        <div>
          <h2 class="text-base/7 font-semibold text-primary-600">
            {{ $t('signature.sectionLabel') }}
          </h2>
          <p class="mt-2 text-pretty font-serif text-4xl font-semibold text-gray-900 sm:text-5xl">
            {{ $t('signature.title') }}
          </p>
          <p class="mt-6 text-lg/8 text-gray-600">
            {{ $t('signature.subtitle') }}
          </p>

          <dl class="mt-10 grid grid-cols-1 gap-x-8 gap-y-6 sm:grid-cols-2">
            <div
              v-for="feature in features"
              :key="feature.title"
              class="relative pl-9"
            >
              <dt class="font-semibold text-gray-900">
                <Icon
                  :name="feature.icon"
                  class="absolute left-0 top-1 size-5 text-primary-600"
                />
                {{ feature.title }}
              </dt>
              <dd class="mt-1 text-sm/6 text-gray-600">
                {{ feature.description }}
              </dd>
            </div>
          </dl>
        </div>

        <div class="flex justify-center max-xs:h-[280px]">
          <div class="relative origin-top scale-[0.6] xs:scale-100">
            <div class="absolute -inset-4 -z-10 rounded-2xl bg-gradient-to-tr from-primary-100 to-primary-50 opacity-50" />
            <div class="relative w-[340px] overflow-hidden rounded-xl bg-white p-8 shadow-xl ring-1 ring-gray-900/10 xs:w-auto">
            <div class="flex items-center gap-3 border-b border-gray-100 pb-4">
              <div class="flex size-10 items-center justify-center rounded-full bg-primary-100">
                <Icon
                  name="lucide:file-signature"
                  class="size-5 text-primary-600"
                />
              </div>
              <div>
                <p class="font-medium text-gray-900">
                  {{ $t('signature.mockup.contractTitle') }}
                </p>
                <p class="text-sm text-gray-500">
                  {{ $t('signature.mockup.pendingSignatures') }}
                </p>
              </div>
            </div>

            <div class="mt-6 space-y-4">
              <div class="flex items-center justify-between rounded-lg bg-green-50 px-4 py-3">
                <div class="flex items-center gap-3">
                  <div class="flex size-8 items-center justify-center rounded-full bg-green-100">
                    <Icon
                      name="lucide:check"
                      class="size-4 text-green-600"
                    />
                  </div>
                  <div>
                    <p class="text-sm font-medium text-gray-900">
                      Marie Dupont
                    </p>
                    <p class="text-xs text-gray-500">
                      {{ $t('signature.mockup.signedDate') }}
                    </p>
                  </div>
                </div>
                <span class="rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-700">{{ $t('signature.mockup.signed') }}</span>
              </div>

              <div class="flex items-center justify-between rounded-lg bg-amber-50 px-4 py-3">
                <div class="flex items-center gap-3">
                  <div class="flex size-8 items-center justify-center rounded-full bg-amber-100">
                    <Icon
                      name="lucide:clock"
                      class="size-4 text-amber-600"
                    />
                  </div>
                  <div>
                    <p class="text-sm font-medium text-gray-900">
                      Pierre Martin
                    </p>
                    <p class="text-xs text-gray-500">
                      {{ $t('signature.mockup.pendingSince') }}
                    </p>
                  </div>
                </div>
                <span class="rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-medium text-amber-700">{{ $t('signature.mockup.pending') }}</span>
              </div>

              <div class="flex items-center justify-between rounded-lg border border-dashed border-gray-200 px-4 py-3">
                <div class="flex items-center gap-3">
                  <div class="flex size-8 items-center justify-center rounded-full bg-gray-100">
                    <Icon
                      name="lucide:user-plus"
                      class="size-4 text-gray-400"
                    />
                  </div>
                  <p class="text-sm text-gray-500">
                    {{ $t('signature.mockup.addSigner') }}
                  </p>
                </div>
              </div>
            </div>

            <div class="mt-6 flex gap-3">
              <button
                type="button"
                class="flex-1 rounded-lg bg-primary-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-primary-500"
              >
                {{ $t('signature.mockup.sendReminder') }}
              </button>
              <button
                type="button"
                class="rounded-lg bg-white px-4 py-2.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
              >
                {{ $t('signature.mockup.viewContract') }}
              </button>
            </div>
          </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
